import {
  AdminInput,
  AdminInputPlaceholder,
  AdminSingleLocationPicker,
  AdminSpinner,
  AdminSwitch,
  Box,
  Button,
  CollapsibleComponent,
  Div,
  ErrorToast,
  H3,
  H5,
  Modal,
  P,
  PARTNER_INTEGRATION_SETUP,
  Radio,
  Separator,
  SuccessToast,
  UserRole,
  api,
  getCurrentBackReceptSize,
  getCurrentFrontReceptSize,
  getLocationById,
  getLocationSettings as getLocationSettingsApi,
  isAdmin,
  isPartner,
  lightTheme,
  read,
  setTimeTo,
  store,
  updateAutoAcceptPrepMinutes,
  reportError,
  useAuth,
} from '@vgrubs/components';
import {
  PreviewReceiptSizeResponse,
  PrintingSettingsResponse,
} from 'components/src/types/api/location.types';
import {
  LocationEntity,
  LocationSettings as LocationSettingsType,
  SimpleLocationEntity,
} from 'components/src/types/location.types';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BankEdit } from '../BankEdit';
import { BusinessLicenseModal } from '../BusinessLicenseModal';
import { PrintingSettingsModal } from '../PrintingSettings';
import { SlipFontSizeModal } from '../SlipFontSizeModal';

export const LocationSettings = () => {
  const navigate = useNavigate();
  const role = useAuth((state) => state.user?.role);
  const [loading, setLoading] = useState(false);
  const [locationDetails, setLocationDetails] =
    useState<LocationSettingsType | null>(null);
  const locationLSNode = 'vgrubs-admin-settings-location';
  const locationFromLS = JSON.parse(read(locationLSNode) as string);
  const [footerMessageModal, setFooterMessageModal] =
    useState<PrintingSettingsResponse | null>(null);
  const [slipFontSizeModal, setSlipFontSizeModal] = useState<{
    data: PreviewReceiptSizeResponse;
    type: 'front' | 'back';
  } | null>(null);

  const [locationData, setLocationData] = useState<LocationEntity | null>(null);

  const [selectedLocation, setSelectedLocation] = useState(
    locationFromLS || null
  );

  const [autoAcceptToggle, setAutoAcceptToggle] = useState(false);
  const [autoAcceptModal, setAutoAcceptModal] = useState(false);
  const [licenseLocationId, setLicenseLocationId] = useState<number | null>(
    null
  );

  const getSlipSize = async (type: 'front' | 'back') => {
    if (type === 'front') {
      const res = await getCurrentFrontReceptSize(selectedLocation.id);
      return res.data;
    }
    const res = await getCurrentBackReceptSize(selectedLocation.id);
    return res.data;
  };

  async function getLocationSettings() {
    try {
      setLoading(true);
      const { data } = await getLocationSettingsApi(selectedLocation.id);

      setLocationDetails(data.data);
      setAutoAcceptToggle(data.data?.prepTime !== null);
      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function getLocationDetails() {
    const today = new Date();
    if (!selectedLocation) return;
    try {
      setLoading(true);
      const { data } = await getLocationById(selectedLocation.id, {
        from: setTimeTo(today, 'start'),
        to: setTimeTo(today, 'end'),
      });

      setLocationData(data.data);
      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  async function getFooterMessage() {
    try {
      setLoading(true);
      const endpoint = isAdmin ? 'footer-message' : 'printing-settings';

      const { data } = await api.get(
        `/v1/locations/${selectedLocation.id}/${endpoint}`
      );
      setFooterMessageModal(data.data);
      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (selectedLocation) {
      getLocationSettings();
    }
  }, [selectedLocation]);

  const handleLocationsSubmit = (l: SimpleLocationEntity) => {
    store(locationLSNode, JSON.stringify(l));
  };

  async function handleGetSlip(type: 'front' | 'back') {
    try {
      setLoading(true);
      const data = await getSlipSize(type);
      setSlipFontSizeModal({ type, data: data.data });
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  function handleToggleAutoAccept(v: boolean) {
    setAutoAcceptToggle(v);
    if (v) {
      setAutoAcceptModal(true);
    } else {
      handleUpdatePrepTime(null);
    }
  }

  async function handleUpdatePrepTime(prepTime: number | null) {
    try {
      setLoading(true);
      const { data } = await updateAutoAcceptPrepMinutes(selectedLocation.id, {
        prepTime,
      });

      SuccessToast(data.message);

      setLocationDetails((prev: LocationSettingsType | null) => {
        if (!prev) return null;
        return {
          ...prev,
          prepTime,
        };
      });

      setAutoAcceptModal(false);

      setLoading(false);
    } catch (error) {
      reportError(error);
      setLoading(false);
    }
  }

  const renderPrepTime = () => {
    if (locationDetails?.prepTime === null) return '';
    if (locationDetails?.prepTime === 0) {
      return 'ASAP';
    }
    return `${locationDetails?.prepTime} min`;
  };

  return (
    <>
      {loading && <AdminSpinner isRootPage />}

      <Div>
        <H3 $m="32px 0" $mSM="20px 0">
          Location Settings
        </H3>

        <AdminSingleLocationPicker
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          handleSubmit={(l) => handleLocationsSubmit(l)}
        />

        <>
          <Box $m="8px 0">
            <Div $flex $alignCenter $between>
              <div>
                <H5 $color={lightTheme.primary} $mb="0">
                  Auto Accept
                </H5>
                <P $color={lightTheme.lightGray} $mb="0">
                  Automatically accept incoming orders
                </P>
              </div>
              <Div>
                <AdminSwitch
                  checked={autoAcceptToggle}
                  onChange={(v) => handleToggleAutoAccept(v.target.checked)}
                />
              </Div>
            </Div>

            <CollapsibleComponent
              expanded={
                !!(locationDetails && locationDetails?.prepTime !== null)
              }
              elementHeight={50}
              marginTop="12px"
            >
              <Separator />
              <Div
                $flex
                $alignCenter
                $gap="8px"
                onClick={() => setAutoAcceptModal(true)}
                $pointer
                $p="8px 0 0 0"
              >
                <P $m="0">Preparation Time:</P>
                <Div $maxWidth="120px">
                  <AdminInputPlaceholder value={renderPrepTime()} $textCenter />
                </Div>
              </Div>
            </CollapsibleComponent>
          </Box>

          <Box $m="8px 0" $flex $alignCenter $between>
            <H5 $color={lightTheme.primary} $mb="0">
              Printing Settings
            </H5>
            <Button
              $gray
              $selected
              $small
              $w="100px"
              onClick={() => getFooterMessage()}
            >
              Edit
            </Button>
          </Box>

          {isPartner && (
            <Box $m="8px 0" $flex $alignCenter $between>
              <H5 $color={lightTheme.primary} $mb="0">
                Bank Details
              </H5>
              <Button
                $gray
                $selected
                $small
                $w="100px"
                onClick={() => getLocationDetails()}
              >
                Edit
              </Button>
            </Box>
          )}

          {/* Owner only: replacing the license answers 403 to a manager. */}
          {isPartner && role === UserRole.partner && (
            <Box $m="8px 0" $flex $alignCenter $between>
              <H5 $color={lightTheme.primary} $mb="0">
                Business License
              </H5>
              <Button
                $gray
                $selected
                $small
                $w="100px"
                onClick={() => setLicenseLocationId(selectedLocation?.id)}
              >
                Edit
              </Button>
            </Box>
          )}

          {/* Owner only: the integration-access API refuses managers. */}
          {isPartner && role === UserRole.partner && (
            <Box $m="8px 0" $flex $alignCenter $between>
              <H5 $color={lightTheme.primary} $mb="0">
                Integration Access
              </H5>
              <Button
                $gray
                $selected
                $small
                $w="100px"
                onClick={() =>
                  // Without a picked location the guide falls back to the main one.
                  navigate(
                    selectedLocation
                      ? `${PARTNER_INTEGRATION_SETUP}?locationId=${selectedLocation.id}`
                      : PARTNER_INTEGRATION_SETUP
                  )
                }
              >
                Open
              </Button>
            </Box>
          )}

          <PrintingSettingsModal
            modalIsOpen={!!footerMessageModal}
            closeModal={() => setFooterMessageModal(null)}
            setLoading={setLoading}
            data={footerMessageModal}
            id={selectedLocation?.id}
            handleGetSlip={handleGetSlip}
          />
          <SlipFontSizeModal
            setModalIsOpen={setSlipFontSizeModal}
            modalIsOpen={slipFontSizeModal}
            closeModal={() => setSlipFontSizeModal(null)}
            setLoading={setLoading}
            id={selectedLocation?.id}
          />
          <BankEdit
            modalIsOpen={locationData}
            closeModal={() => {
              setLocationData(null);
            }}
            setLoading={setLoading}
            data={locationData}
            setData={setLocationData}
          />

          <BusinessLicenseModal
            locationId={licenseLocationId}
            closeModal={() => setLicenseLocationId(null)}
          />

          <AutoAcceptModal
            prepTime={locationDetails?.prepTime || null}
            autoAcceptModal={autoAcceptModal}
            setAutoAcceptModal={setAutoAcceptModal}
            handleUpdatePrepTime={handleUpdatePrepTime}
            handleToggleAutoAccept={handleToggleAutoAccept}
            setToggleAutoAccept={setAutoAcceptToggle}
          />
        </>
      </Div>
    </>
  );
};

const AutoAcceptModal = ({
  prepTime,
  autoAcceptModal,
  setAutoAcceptModal,
  handleUpdatePrepTime,
  handleToggleAutoAccept,
  setToggleAutoAccept,
}: {
  prepTime: number | null;
  autoAcceptModal: boolean;
  setAutoAcceptModal: (v: boolean) => void;
  handleUpdatePrepTime: (prepTime: number | null) => void;
  handleToggleAutoAccept: (v: boolean) => void;
  setToggleAutoAccept: (v: boolean) => void;
}) => {
  const [autoAcceptTime, setAutoAcceptTime] = useState('');
  const [autoAcceptTimeType, setAutoAcceptTimeType] = useState<
    'minutes' | 'ASAP'
  >('minutes');

  function handleUpdateAutoAcceptTime() {
    if (
      autoAcceptTimeType === 'minutes' &&
      (!autoAcceptTime || autoAcceptTime === '0')
    ) {
      ErrorToast('Please enter a preparation time in minutes');
      return;
    }

    if (autoAcceptTimeType === 'minutes') {
      handleUpdatePrepTime(Number(autoAcceptTime));
    } else {
      handleUpdatePrepTime(0);
    }
  }

  useEffect(() => {
    if (prepTime === 0) {
      setAutoAcceptTimeType('ASAP');
      setAutoAcceptTime('');
    } else if (prepTime !== null) {
      setAutoAcceptTimeType('minutes');
      setAutoAcceptTime(prepTime?.toString() || '');
    } else {
      setAutoAcceptTimeType('minutes');
      setAutoAcceptTime('');
    }
  }, [prepTime]);

  return (
    <Modal
      isOpen={autoAcceptModal}
      closeModal={() => {
        setAutoAcceptModal(false);
        if (prepTime === null) {
          setToggleAutoAccept(false);
        }
      }}
      logo={false}
      width="300px"
    >
      <H3>Preparation Time</H3>

      <Div $flex $alignCenter $between $height="50px">
        <Div
          $flex
          $alignCenter
          $gap="8px"
          onClick={() => setAutoAcceptTimeType('minutes')}
          $pointer
        >
          <Radio checked={autoAcceptTimeType === 'minutes'} />
          <P $m="0">Minutes</P>
        </Div>

        <Div $maxWidth="120px">
          <AdminInput
            onFocus={() => setAutoAcceptTimeType('minutes')}
            placeholder="Enter here"
            value={autoAcceptTime}
            onChange={(e) => setAutoAcceptTime(e.target.value)}
            $textCenter
          />
        </Div>
      </Div>

      {/* <Separator /> */}

      <Div
        $flex
        $alignCenter
        $gap="8px"
        onClick={() => setAutoAcceptTimeType('ASAP')}
        $pointer
        $height="50px"
      >
        <Radio checked={autoAcceptTimeType === 'ASAP'} />
        <P $m="0">ASAP</P>
      </Div>

      <Div $flex $gap="8px" $mt="24" $column>
        <Button $selected $w="100%" onClick={handleUpdateAutoAcceptTime}>
          Save
        </Button>
        <Button
          $selected
          $w="100%"
          $gray
          onClick={() => {
            setAutoAcceptModal(false);
            if (prepTime === null) {
              setToggleAutoAccept(false);
            }
          }}
        >
          Cancel
        </Button>
      </Div>
    </Modal>
  );
};
